import {
  IAddressDiscount,
  IPageableRechargeAddressResponse,
  IRechargeAddress,
  IRechargeAddressGetParams,
  ISingleRechargeAddressResponse,
  TCreateRechargeAddressRequest,
  TUpdateRechargeAddressRequest,
} from "@recharge";
import axios from "axios";
import { Logger } from "@nestjs/common";

import { RechargeResourceEndpoint } from "../../RechargeEndpoint";
import { RechargeAccessTokenManager } from "../RechargeAccessTokenManager";

export class RechargeAddressesClient extends RechargeResourceEndpoint<
ISingleRechargeAddressResponse,
IPageableRechargeAddressResponse,
IRechargeAddressGetParams,
IRechargeAddress
> {

  public constructor(
    rechargeKeyManager: RechargeAccessTokenManager,
    baseUrl = "https://api.rechargeapps.com",
  ) {
    super(
      axios.create({ baseURL: baseUrl }),
      rechargeKeyManager.getNextAccessToken.bind(rechargeKeyManager),
      "/addresses",
      new Logger(RechargeAddressesClient.name),
    );
  }

  protected override singleGetter(t: ISingleRechargeAddressResponse): IRechargeAddress {
    return t.address;
  }

  protected override multiGetter(t: IPageableRechargeAddressResponse): IRechargeAddress[] {
    return t.addresses;
  }

  /** Attach the given discount to the given address.
   * Any other attached discount will be removed.
   * @param rechargeAddressId The address to update
   * @param rechargeDiscountId The discount to attach to the address
   * @param commit if true, the charge MIGHT get regenerated more quickly,
   * but the request might time out if regeneration takes too long.
   * This should only be used during checkouts. */
  public async addDiscount(
    rechargeAddressId: number,
    rechargeDiscountId: number,
    commit: true | undefined,
  ): Promise<IRechargeAddress> {
    const response = await this.requestWithRetry<ISingleRechargeAddressResponse, { discounts: [IAddressDiscount] }>(
      {
        url: `${this.resourcePath}/${rechargeAddressId}`,
        config: {
          headers: this.getHeaders(),
          method: "PUT",
          data: {
            discounts: [ { id: rechargeDiscountId } ],
          },
          params: {
            commit,
          },
        },
        newKeyOnRetry: true,
        // This simply attaches a discount to an address, so it should be fine to retry.
        // Worst case someone is trying to modify the address at the same time, which is probably fine,
        // as long as whoever is modifying it isn't also trying to attach a different discount.
        retryGatewayTimeout: true,
        retryConflict: true,
        truncateLongBody: false,
      },
    );
    return response.data.address;
  }

  /** Any currently attached discount will be removed.
   * It should be fine to call this even if no discount is currently attached.
   * @param rechargeAddressId The address to update
   * @param commit if true, the charge MIGHT get regenerated more quickly,
   * but the request might time out if regeneration takes too long.
   * This should only be used during checkouts. */
  public async removeDiscount(
    rechargeAddressId: number,
    commit: true | undefined,
  ): Promise<IRechargeAddress> {
    const response = await this.requestWithRetry<ISingleRechargeAddressResponse, { discounts: [] }>(
      {
        url: `${this.resourcePath}/${rechargeAddressId}`,
        config: {
          headers: this.getHeaders(),
          method: "PUT",
          data: { discounts: [] },
          params: {
            commit,
          },
        },
        newKeyOnRetry: true,
        // Same as addDiscount, retrying should be fine
        retryGatewayTimeout: true,
        retryConflict: true,
        truncateLongBody: false,
      },
    );
    return response.data.address;
  }

  public async createAddress(
    toCreate: TCreateRechargeAddressRequest,
  ): Promise<IRechargeAddress> {
    const response = await this.requestWithRetry<
    ISingleRechargeAddressResponse,
    TCreateRechargeAddressRequest
    >(
      {
        url: `${this.resourcePath}`,
        config: {
          headers: this.getHeaders(),
          method: "POST",
          data: toCreate,
        },
        newKeyOnRetry: true,
        // We don't want to retry create, there's a high chance of creating duplicates
        retryGatewayTimeout: false,
        retryConflict: false,
        truncateLongBody: false,
      },
    );
    return response.data.address;
  }

  public async updateAddress(
    rechargeAddressId: number | bigint,
    toUpdate: TUpdateRechargeAddressRequest,
  ): Promise<IRechargeAddress> {
    const response = await this.requestWithRetry<
    ISingleRechargeAddressResponse,
    TUpdateRechargeAddressRequest
    >({
      url: `${this.resourcePath}/${rechargeAddressId}`,
      config: {
        headers: this.getHeaders(),
        method: "PUT",
        data: toUpdate,
      },
      newKeyOnRetry: true,
      // We don't want to retry sync, we'll just let the sync job run later
      retryGatewayTimeout: false,
      retryConflict: false,
      truncateLongBody: false,
    });
    return response.data.address;
  }

  public async deleteAddress(
    rechargeAddressId: number,
  ): Promise<Record<string, never>> {
    const response = await this.requestWithRetry<Record<string, never>, never>(
      {
        url: `${this.resourcePath}/${rechargeAddressId}`,
        config: {
          headers: this.getHeaders(),
          method: "DELETE",
        },
        newKeyOnRetry: true,
        // Retrying delete is fine,
        // worst case we get an error that the address has already been deleted
        retryGatewayTimeout: true,
        retryConflict: true,
        truncateLongBody: false,
      },
    );
    return response.data;
  }

}
